import { useEffect, useState } from 'react';
import api from '../lib/api';
import { getStoredPromo } from '../lib/promo';

/**
 * Reads the silently-stored promo code (captured from `?promo=XXX`) and, if
 * present, asks the backend whether it's valid + what % off it gives.
 *
 * Returns `{ code, percentOff, valid, loading, apply }`.
 * `apply(inrAmount)` returns the discounted INR amount (or the original if no
 * valid promo). Pages use it to render a strikethrough original price + the
 * discounted price + a % OFF badge, but only when `valid === true`.
 *
 * The backend never leaks the full code list — `/api/coupons/preview` only
 * confirms/denies the single submitted code.
 */
export default function usePromoPreview() {
  const [state, setState] = useState({ code: null, percentOff: 0, valid: false, loading: true });

  useEffect(() => {
    let cancelled = false;
    const stored = getStoredPromo();
    if (!stored) {
      setState({ code: null, percentOff: 0, valid: false, loading: false });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post('/coupons/preview', { code: stored });
        if (cancelled) return;
        setState({
          code: data.valid ? data.code : null,
          percentOff: data.valid ? Number(data.percent_off || 0) : 0,
          valid: !!data.valid,
          loading: false,
        });
      } catch (_) {
        if (!cancelled) setState({ code: null, percentOff: 0, valid: false, loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const apply = (inr) => {
    const n = Number(inr) || 0;
    if (!state.valid || !state.percentOff) return n;
    const discounted = n * (1 - state.percentOff / 100);
    return Math.max(Math.round(discounted * 100) / 100, 1);
  };

  return { ...state, apply };
}
